/**
 * RiskScoreBadge (component-inventory.md §3.1) — the computed risk score (0–100) shown as
 * a badge tinted by its band (design-system.md §2.4 risk tokens: low/medium/high/critical
 * map to the §10 severity tones). When a case has no score yet, a muted "No score"
 * placeholder renders instead (§13 — never a bare 0 that reads as a real score).
 */
import type { JSX } from 'react';

import type { RiskBand } from '../../lib/api.ts';
import { Badge, type BadgeTone } from '../ui/badge.tsx';

const BAND_TONE: Record<RiskBand, BadgeTone> = {
  low: 'low',
  medium: 'medium',
  high: 'high',
  critical: 'critical',
};

/** Derive a band from a raw 0–100 score when the API hasn't supplied `risk_band`. */
function bandFromScore(score: number): RiskBand {
  if (score >= 75) return 'critical';
  if (score >= 50) return 'high';
  if (score >= 25) return 'medium';
  return 'low';
}

export function RiskScoreBadge({
  score,
  band,
}: {
  score?: number | null | undefined;
  band?: RiskBand | null | undefined;
}): JSX.Element {
  if (score === null || score === undefined) {
    return <Badge tone="muted">No score</Badge>;
  }
  const resolvedBand = band ?? bandFromScore(score);
  return <Badge tone={BAND_TONE[resolvedBand]}>{`Risk ${score} · ${resolvedBand}`}</Badge>;
}
